import React, { useState, useEffect } from 'react';
import { DollarSign, ChevronLeft, ChevronRight, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { apiService } from '../../services/api.service';

interface SummaryItem {
  reason: string;
  count: number;
  overpaid_rupees: number;
  total_paid_rupees: number;
}

interface Transaction {
  beneficiaryId: string;
  stage: string;
  payment_with: string;
  bankProcessedDate: string;
  amount_rupees: number;
  credit_reference: string;
  batchId: string;
  reconciliationId: string;
  batch_file_name: string;
  response_file_name: string;
}

interface OverpaidRecord {
  beneficiaryId: string;
  name: string;
  payment_methods: string;
  reason: string;
  bl_count: number;
  rl_count: number;
  rc_count: number;
  total_paid_rupees: number;
  expected_rupees: number;
  overpaid_rupees: number;
  transactions: Transaction[];
}

interface OverpaidData {
  summary: SummaryItem[];
  records: OverpaidRecord[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  total_overpaid_rupees: number;
}

const reasonLabels: Record<string, string> = {
  DUPLICATE_BL: 'Duplicate BL Payment',
  DUPLICATE_RL: 'Duplicate RL Payment',
  DUPLICATE_RC: 'Duplicate RC Payment',
  DUPLICATE_BL_RL: 'Duplicate BL + RL',
  DUPLICATE_BL_RC: 'Duplicate BL + RC',
  DUPLICATE_RL_RC: 'Duplicate RL + RC',
  Other: 'Other',
};

const reasonColors: Record<string, string> = {
  DUPLICATE_BL: 'bg-blue-100 text-blue-800 border-blue-300',
  DUPLICATE_RL: 'bg-orange-100 text-orange-800 border-orange-300',
  DUPLICATE_RC: 'bg-red-100 text-red-800 border-red-300',
  DUPLICATE_BL_RL: 'bg-purple-100 text-purple-800 border-purple-300',
  DUPLICATE_BL_RC: 'bg-pink-100 text-pink-800 border-pink-300',
  DUPLICATE_RL_RC: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  Other: 'bg-gray-100 text-gray-800 border-gray-300',
};

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-IN').format(num);
};

const formatRupees = (rupees: number): string => {
  if (rupees >= 10000000) return `${(rupees / 10000000).toFixed(2)} Cr`;
  if (rupees >= 100000) return `${(rupees / 100000).toFixed(2)} L`;
  if (rupees >= 1000) return `${(rupees / 1000).toFixed(1)} K`;
  return `${rupees}`;
};

export default function OverpaidBeneficiaries() {
  const [data, setData] = useState<OverpaidData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [page, setPage] = useState(1);
  const [searchId, setSearchId] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const limit = 50;

  const fetchData = async (reason: string, pageNum: number) => {
    setLoading(true);
    try {
      const result = await apiService.getOverpaid(reason || undefined, pageNum, limit);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch overpaid beneficiaries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(selectedReason, page);
  }, [selectedReason, page]);

  const handleReasonFilter = (reason: string) => {
    setSelectedReason(reason === selectedReason ? '' : reason);
    setPage(1);
  };

  const filteredRecords = data?.records?.filter(r =>
    !searchId || r.beneficiaryId.toLowerCase().includes(searchId.toLowerCase()) ||
    (r.name && r.name.toLowerCase().includes(searchId.toLowerCase()))
  ) || [];

  const totalOverpaid = data?.summary?.reduce((sum, s) => sum + s.overpaid_rupees, 0) || 0;
  const totalBeneficiaries = data?.summary?.reduce((sum, s) => sum + s.count, 0) || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 bg-red-500 rounded-lg">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Overpaid Beneficiaries</h1>
            <p className="text-sm text-gray-600">
              Beneficiaries who received duplicate stage payments (total paid &gt; 5,00,000)
            </p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-sm text-gray-500">Total Overpayment</p>
            <p className="text-2xl font-bold text-red-600">{formatRupees(totalOverpaid)}</p>
            <p className="text-xs text-gray-500">{formatNumber(totalBeneficiaries)} beneficiaries</p>
          </div>
        </div>

        {/* Summary Cards */}
        {data?.summary && data.summary.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.summary.map((item) => (
              <div
                key={item.reason}
                onClick={() => handleReasonFilter(item.reason)}
                className={`rounded-xl p-5 border-2 cursor-pointer transition-all ${
                  selectedReason === item.reason
                    ? 'ring-2 ring-blue-500 border-blue-500 shadow-md'
                    : `${reasonColors[item.reason] || 'bg-gray-100 text-gray-800 border-gray-300'} hover:shadow-md`
                }`}
              >
                <p className="text-sm font-medium mb-1">{reasonLabels[item.reason] || item.reason}</p>
                <p className="text-3xl font-bold">{formatNumber(item.count)}</p>
                <div className="mt-2 space-y-0.5">
                  <p className="text-xs opacity-75">
                    Overpaid: <span className="font-bold text-red-700">{formatRupees(item.overpaid_rupees)}</span>
                  </p>
                  <p className="text-xs opacity-75">
                    Total Paid: {formatRupees(item.total_paid_rupees)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Search + Info */}
        <div className="flex items-center justify-between">
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Beneficiary ID or Name..."
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
          <p className="text-sm text-gray-600">
            Showing: <span className="font-bold">{formatNumber(data?.total || 0)}</span> beneficiaries
            {selectedReason && (
              <span className="ml-2 text-blue-600">
                (filtered: {reasonLabels[selectedReason] || selectedReason})
              </span>
            )}
          </p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-semibold text-gray-700 w-8"></th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Beneficiary ID</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Reason</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-700">BL</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-700">RL</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-700">RC</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">Total Paid</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">Expected</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-700">Overpaid</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Payment Via</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((rec, idx) => (
                    <React.Fragment key={rec.beneficiaryId}>
                      <tr
                        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                          expandedRow === rec.beneficiaryId ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => setExpandedRow(expandedRow === rec.beneficiaryId ? null : rec.beneficiaryId)}
                      >
                        <td className="px-4 py-3">
                          {expandedRow === rec.beneficiaryId
                            ? <ChevronUp className="w-4 h-4 text-gray-400" />
                            : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{(data!.page - 1) * limit + idx + 1}</td>
                        <td className="px-4 py-3 font-mono text-xs">{rec.beneficiaryId}</td>
                        <td className="px-4 py-3">{rec.name || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${reasonColors[rec.reason] || 'bg-gray-100'}`}>
                            {reasonLabels[rec.reason] || rec.reason}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={rec.bl_count > 1 ? 'text-red-600 font-bold' : ''}>{rec.bl_count}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={rec.rl_count > 1 ? 'text-red-600 font-bold' : ''}>{rec.rl_count}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={rec.rc_count > 1 ? 'text-red-600 font-bold' : ''}>{rec.rc_count}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs">
                          {formatNumber(rec.total_paid_rupees)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs text-gray-500">
                          {formatNumber(rec.expected_rupees)}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-red-600">
                          +{formatNumber(rec.overpaid_rupees)}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {(rec.payment_methods || '').replace('BANK', 'Kotak')}
                        </td>
                      </tr>

                      {/* Expandable Transaction Details */}
                      {expandedRow === rec.beneficiaryId && rec.transactions && rec.transactions.length > 0 && (
                        <tr>
                          <td colSpan={12} className="px-4 py-0">
                            <div className="bg-gray-50 rounded-lg p-4 mb-3 ml-8">
                              <p className="text-xs font-semibold text-gray-700 mb-2">
                                Transaction Details ({rec.transactions.length} payments)
                              </p>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500">
                                    <th className="text-left py-1 pr-3">Stage</th>
                                    <th className="text-left py-1 pr-3">Payment Via</th>
                                    <th className="text-left py-1 pr-3">Date</th>
                                    <th className="text-right py-1 pr-3">Amount</th>
                                    <th className="text-left py-1 pr-3">UTR / Reference</th>
                                    <th className="text-left py-1 pr-3">Batch ID</th>
                                    <th className="text-left py-1">Response File</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rec.transactions.map((txn, tidx) => (
                                    <tr key={tidx} className="border-t border-gray-200">
                                      <td className="py-1.5 pr-3 font-medium">{txn.stage}</td>
                                      <td className="py-1.5 pr-3">
                                        {(txn.payment_with || '').replace('BANK', 'Kotak')}
                                      </td>
                                      <td className="py-1.5 pr-3 text-gray-600">
                                        {txn.bankProcessedDate ? new Date(txn.bankProcessedDate).toLocaleDateString('en-IN') : '-'}
                                      </td>
                                      <td className="py-1.5 pr-3 text-right font-mono">
                                        {formatNumber(txn.amount_rupees)}
                                      </td>
                                      <td className="py-1.5 pr-3 font-mono text-gray-500">
                                        {txn.credit_reference || '-'}
                                      </td>
                                      <td className="py-1.5 pr-3 font-mono text-gray-500" title={txn.batchId || ''}>
                                        {txn.batchId ? txn.batchId.substring(0, 8) + '...' : '-'}
                                      </td>
                                      <td className="py-1.5 font-mono text-gray-500 max-w-[200px] truncate" title={txn.response_file_name || txn.batch_file_name || ''}>
                                        {txn.response_file_name || txn.batch_file_name || '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {filteredRecords.length === 0 && (
                    <tr>
                      <td colSpan={12} className="px-4 py-8 text-center text-gray-500">No records found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Page {data.page} of {data.total_pages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <button
                onClick={() => setPage(Math.min(data.total_pages, page + 1))}
                disabled={page >= data.total_pages}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
